/**
 * TitleScene — the front door of the game.
 *
 * Responsibilities (build-plan-v2 §2.3, AGE-1310 design spec):
 *   - Render the Wave-1 title-card art with a graceful styled-text fallback.
 *   - Walk a character along the dirt-road path in the title art (Bezier waypoints).
 *   - Display game title + subtitle over the sky area.
 *   - Offer "Enter the world" with a breathing pulse.
 *   - Group UI controls (mute, volume, settings, devworld) in a panel.
 *   - Offer a mute toggle that persists via localStorage and the registry.
 */

import * as Phaser from 'phaser';
import {
  TITLE_SCENE_KEY,
  CHARACTER_CREATION_SCENE_KEY,
  THRESHOLD_SCENE_KEY,
  PAINTED_WORLD_COORDINATOR_SCENE_KEY,
  SCENE_SELECTOR_SCENE_KEY,
} from './sceneKeys';
import { loadSettings, saveSettings } from '../ui/SettingsPersistence';
import {
  loadAllSlots,
  deleteSlot,
  formatPlaytime,
  formatLastPlayed,
  getSlotCount,
  type SaveSlotData,
} from '../ui/SaveSlotPersistence';
import { isC5UiEnabled } from '../ui/c5/c5Flag';
import { buildC5SlotCard } from '../ui/c5/C5SaveSlots';
import { CollisionGrid, Pathfinder, type PerspectiveProfile } from '@game/shared';
import { ParallaxLayerManager } from './painted/ParallaxLayerManager';
import { WalkerAnimator, interpolateWaypoints, type WalkerAnimationDef } from '../game/WalkerAnimator';

/** localStorage key for the persisted mute state. Mirrors the registry. */
const MUTE_STORAGE_KEY = 'rpg-gibli:audio-muted';

/** Title-card image dimensions (matches `title-card.json`). */
const TITLE_CARD_W = 1344;
const TITLE_CARD_H = 768;

/** A button hit-rect height — large enough for a comfortable click target. */
const BUTTON_H = 36;
const BUTTON_W = 220;

/** Volume slider dimensions. */
const SLIDER_W = 120;
const SLIDER_H = 6;
const SLIDER_TRACK_COLOR = 0x3a3a5e;
const SLIDER_FILL_COLOR = 0xeae0c8;

/** PS1-style slow fade-in duration (ms). */
const FADE_IN_MS = 1200;

/** Walking animation frame rate for walk-left/right (6-frame keyframe cycle). */
const WALKER_LEFT_ANIM_FPS = 10;

/** Delay before UI panel appears after the art fades in (ms). */
const BUTTON_DELAY_MS = 600;

/** Delay after fade-in before the title auto-dissolves into gameplay (ms). */
const AUTO_ENTER_DELAY_MS = 2500;

/** Delay before title text fades in (ms) — slightly after art. */
const TITLE_TEXT_DELAY_MS = 400;

/** Painterly serif used for all title chrome (loaded via `index.html`). */
const TITLE_FONT = "'Almendra', serif";

/** Cream-on-deep-blue palette borrowed from the rest of the v2 UI work. */
const COLOR_INK = '#eae0c8';
const COLOR_INK_HOVER = '#ffffff';
const COLOR_BG_DEEP = '#1a1a2e';

/** UI panel styling. */
const PANEL_BG_COLOR = 0x1a1a2e;
const PANEL_BG_ALPHA = 0.45;
const PANEL_BORDER_COLOR = 0xeae0c8;
const PANEL_BORDER_WIDTH = 1;
const PANEL_MARGIN = 12;
const PANEL_PADDING = 8;
const PANEL_BORDER_RADIUS = 6;

// ── Menu state machine ───────────────────────────────────────────────────────

const enum MenuState {
  MAIN_MENU,
  SAVE_SLOTS,
  DELETE_CONFIRM,
  ENTERING_WORLD,
}

/** Save slot card dimensions. */
const SLOT_CARD_W = 340;
const SLOT_CARD_H = 80;
const SLOT_CARD_GAP = 16;
const SLOT_CARD_BG = 0x1a1a2e;
const SLOT_CARD_BG_ALPHA = 0.7;
const SLOT_CARD_BORDER = 0xeae0c8;
const SLOT_CARD_BORDER_HOVER = 0xffffff;
const SLOT_EMPTY_TEXT = 'New Journey';

/** Confirm dialog dimensions. */
const CONFIRM_W = 300;
const CONFIRM_H = 140;

// ── Dissolve transition timing (GDD §3.3) ──────────────────────────────────

/** Phase 1: logo + enter button fade out. */
const DISSOLVE_P1_MS = 400;
/** Phase 2: options panel + title text dissolve. */
const DISSOLVE_P2_MS = 400;
/** Phase 3: walker stops loop, turns to idle. */
const DISSOLVE_P3_MS = 400;
/** Phase 4: brief WASD indicator appears. */
const DISSOLVE_P4_MS = 400;
/** Timeout before showing reassurance text. */
const DISSOLVE_REASSURE_MS = 5000;
/** Timeout before offering return to title. */
const DISSOLVE_BAIL_MS = 12000;

// ── Walker path system (pathfinder-backed) ───────────────────────────────────

/** Walker states for the dirt-road path loop. */
const enum WalkerState {
  WALKING_UP,
  PAUSED_TALKING,
  WALKING_DOWN,
  OFF_SCREEN_WAIT,
  IDLE_TITLE,
  PLAYER_CONTROLLED,
}

/** Player movement speed in normalized units per second. */
const PLAYER_MOVE_SPEED = 0.06;

/** Cottage door interaction zone (normalized coords). */
const COTTAGE_DOOR_NX = 0.48;
const COTTAGE_DOOR_NY = 0.72;
const COTTAGE_INTERACT_RADIUS = 0.06;

/**
 * Title-card collision grid dimensions.
 * The title card art is 1344×768; a 48 px tile size gives a 28×16 grid.
 * Road tiles are marked walkable (0); sky/hills/water are solid (1).
 */
const TITLE_GRID_TILE = 48;
const TITLE_GRID_W = Math.ceil(TITLE_CARD_W / TITLE_GRID_TILE); // 28
const TITLE_GRID_H = Math.ceil(TITLE_CARD_H / TITLE_GRID_TILE); // 16

/** Tile coords for the walker's start (bottom of road) and end (cottage door). */
const WALKER_START_TILE = { x: 14, y: 15 };
const WALKER_END_TILE = { x: 13, y: 11 };

/**
 * Fallback waypoints used when the pathfinder can't compute a route
 * (e.g. if the grid constants are misconfigured).
 */
const FALLBACK_PATH_WAYPOINTS: Array<{ x: number; y: number }> = [
  { x: 0.50, y: 0.97 },
  { x: 0.50, y: 0.90 },
  { x: 0.49, y: 0.84 },
  { x: 0.48, y: 0.78 },
  { x: 0.48, y: 0.72 },
];

/**
 * Forced-perspective profile for the title card.
 *
 * Horizon sits well above the cottage roof (~y=0.45). The eased curve gives
 * a strong foreground bias so the walker reads ~2× tall at the road bottom
 * (taller than the grass overlay) and ~1× near the cottage door, where he
 * matches the painted cottage's apparent scale.
 */
const TITLE_CARD_PERSPECTIVE: PerspectiveProfile = {
  horizonY: 0.45,
  nearY: 1.05,
  minScale: 0.50,
  maxScale: 2.30,
  curve: 1.5,
};

/** Speed in normalized units per second along the path. */
const WALKER_PATH_SPEED = 0.04;

/** Duration of the talking pause in ms (2-3 loops at 3 FPS = ~6-9s). */
const WALKER_TALK_DURATION_MS = 8000;

/** Duration to wait off-screen before restarting the loop (ms). */
const WALKER_OFFSCREEN_WAIT_MS = 4000;

interface TitleButton {
  hit: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
}

export class TitleScene extends Phaser.Scene {
  public static readonly KEY = TITLE_SCENE_KEY;

  private muteButton: TitleButton | null = null;
  private titleMusic: Phaser.Sound.BaseSound | null = null;
  private walkerAnimator: WalkerAnimator | null = null;

  /** AGE-1316 — references for enter animation. */
  private enterButton: TitleButton | null = null;
  private uiPanel: Phaser.GameObjects.Container | null = null;
  private enterInProgress = false;

  /** Background animation overlay references. */
  private parallaxManager: ParallaxLayerManager | null = null;
  private grassOverlay: Phaser.GameObjects.Image | null = null;
  private grassMaskGfx: Phaser.GameObjects.Graphics | null = null;
  private logoImage: Phaser.GameObjects.Image | null = null;

  /** Volume slider state. */
  private sliderTrack: Phaser.GameObjects.Rectangle | null = null;
  private sliderFill: Phaser.GameObjects.Rectangle | null = null;
  private sliderHit: Phaser.GameObjects.Rectangle | null = null;
  private sliderLabel: Phaser.GameObjects.Text | null = null;
  private musicVolume = 0.5;

  /** Menu state machine. */
  private menuState: MenuState = MenuState.MAIN_MENU;
  private saveSlotContainer: Phaser.GameObjects.Container | null = null;
  private confirmContainer: Phaser.GameObjects.Container | null = null;
  private pendingDeleteIndex = -1;

  /** Title text references for dissolve phase 2. */
  private titleTextObjects: Phaser.GameObjects.GameObject[] = [];

  /** Dissolve transition state. */
  private dissolveActive = false;
  private overworldReady = false;
  private dissolvePhaseComplete = false;
  private wasdIndicator: Phaser.GameObjects.Text | null = null;
  private reassuranceText: Phaser.GameObjects.Text | null = null;
  private returnButton: TitleButton | null = null;
  private dissolveTimers: Phaser.Time.TimerEvent[] = [];

  /** High-level walker state (autonomous walking delegated to WalkerAnimator). */
  private walkerState: WalkerState = WalkerState.OFF_SCREEN_WAIT;

  /** Pathfinder-computed waypoints (normalized 0–1) for the walker route. */
  private walkerPathWaypoints: Array<{ x: number; y: number }> = FALLBACK_PATH_WAYPOINTS;

  /** Player input state. */
  private cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null;
  private wasdKeys: { W: Phaser.Input.Keyboard.Key; A: Phaser.Input.Keyboard.Key; S: Phaser.Input.Keyboard.Key; D: Phaser.Input.Keyboard.Key } | null = null;
  private playerNX = 0.50;
  private playerNY = 0.80;
  private isPlaying = false;
  private clickTargetPath: Array<{ x: number; y: number }> | null = null;
  private clickPathProgress = 0;
  private collisionGrid: CollisionGrid | null = null;
  private pathfinder: Pathfinder | null = null;
  private cottageGlow: Phaser.GameObjects.Ellipse | null = null;
  private cottagePrompt: Phaser.GameObjects.Text | null = null;
  private nearCottage = false;

  /** Tracks whether the player has moved at least once after control transfer. */
  private hasMovedOnce = false;

  constructor() {
    super({ key: TITLE_SCENE_KEY });
  }

  preload(): void {
    if (!this.cache.audio.exists('title-theme')) {
      this.load.audio('title-theme', 'assets/audio/music/title-theme-v1.mp3');
    }

    // Waker sprite sheets (128×128 frames, horizontal strips)
    const frameConfig = { frameWidth: 128, frameHeight: 128 };
    this.load.spritesheet('waker-walk-up', 'assets/sprites/waker/waker-walk-up.png', frameConfig);
    this.load.spritesheet('waker-walk-down', 'assets/sprites/waker/waker-walk-down.png', frameConfig);
    this.load.spritesheet('waker-walk-left', 'assets/sprites/waker/waker-walk-left.png', frameConfig);
    this.load.spritesheet('waker-idle', 'assets/sprites/waker/waker-idle.png', frameConfig);
    this.load.spritesheet('waker-talk', 'assets/sprites/waker/waker-talk.png', frameConfig);

    // Painted logo
    this.load.image('game-logo', 'art/ui/game-logo.png');

    // Background animation overlays
    this.load.image('title-clouds-1', 'art/ui/title-clouds-1.png');
    this.load.image('title-clouds-2', 'art/ui/title-clouds-2.png');
    this.load.image('title-clouds-3', 'art/ui/title-clouds-3.png');
    this.load.image('title-grass-overlay', 'art/ui/title-grass-overlay.png');
    this.load.image('title-trees-overlay', 'art/ui/title-trees-overlay.png');
  }

  create(data?: {
    enteringWorld?: boolean;
    showSaveSlots?: boolean;
    slotIndex?: number;
    characterName?: string;
    vocation?: string;
    firstDay?: boolean;
  }): void {
    this.enterInProgress = false;
    this.dissolveActive = false;
    this.overworldReady = false;
    this.dissolvePhaseComplete = false;
    this.hasMovedOnce = false;
    this.isPlaying = false;
    this.titleTextObjects = [];
    this.dissolveTimers = [];
    this.wasdIndicator = null;
    this.reassuranceText = null;
    this.returnButton = null;
    this.cameras.main.setBackgroundColor(COLOR_BG_DEEP);
    this.renderTitleArt();
    this.renderTitleText();

    // ── Entering-world fast path (from CharacterCreationScene) ──
    // Rebuild the painted background but skip menu chrome and immediately
    // trigger the seamless dissolve into OverworldScene.
    if (data?.enteringWorld) {
      const enterBtn = this.renderEnterButton();
      this.enterButton = enterBtn;
      const uiPanel = this.renderUIPanel();
      this.uiPanel = uiPanel;
      this.spawnWalker();
      this.transitionToWorld({
        slotIndex: data.slotIndex ?? 0,
        characterName: data.characterName ?? '',
        vocation: data.vocation ?? '',
        firstDay: data.firstDay ?? false,
      });
      return;
    }

    // ── Save-slot fast path (returning from cottage) ──
    if (data?.showSaveSlots) {
      const enterBtn = this.renderEnterButton();
      this.enterButton = enterBtn;
      const uiPanel = this.renderUIPanel();
      this.uiPanel = uiPanel;
      enterBtn.hit.setAlpha(0);
      enterBtn.label.setAlpha(0);
      uiPanel.setAlpha(0);
      this.cameras.main.fadeIn(600, 0, 0, 0);
      this.spawnWalker();
      this.startTitleMusic();
      this.time.delayedCall(300, () => this.buildSaveSlotUI());
      return;
    }

    // Create UI panel hidden — it fades in after the art
    const uiPanel = this.renderUIPanel();
    this.uiPanel = uiPanel;
    uiPanel.setAlpha(0);

    // PS1-style slow fade from black
    this.cameras.main.fadeIn(FADE_IN_MS, 0, 0, 0);

    // Stagger UI panel in after art is mostly visible
    this.time.delayedCall(BUTTON_DELAY_MS, () => {
      this.tweens.add({
        targets: uiPanel,
        alpha: 1,
        duration: 800,
        ease: 'Sine.easeIn',
      });
    });

    this.startTitleMusic();
    this.spawnWalker();

    // Auto-dissolve into gameplay after the title lingers
    this.time.delayedCall(AUTO_ENTER_DELAY_MS, () => {
      if (!this.enterInProgress && this.menuState === MenuState.MAIN_MENU) {
        this.handleEnter();
      }
    });
  }

  // ── Walker path system ──────────────────────────────────────────────────────

  /**
   * Build a CollisionGrid + Pathfinder representing the title card's dirt road.
   * Sky, hills, and water are solid; the road corridor is walkable.
   * Returns normalized (0–1) waypoints from WALKER_START_TILE to WALKER_END_TILE.
   */
  private computeWalkerPath(): Array<{ x: number; y: number }> {
    const w = TITLE_GRID_W;
    const h = TITLE_GRID_H;
    const data = new Array(w * h).fill(1); // all solid by default

    const markWalkable = (row: number, colStart: number, colEnd: number) => {
      for (let c = colStart; c <= colEnd; c++) {
        if (c >= 0 && c < w && row >= 0 && row < h) {
          data[row * w + c] = 0;
        }
      }
    };

    // Road corridor matching the title-card art (bottom → cottage).
    // Row 0 = top of card, row 15 = bottom.
    // Rows 0-9 are sky/roof — NOT walkable.
    markWalkable(15, 13, 15); // road entry bottom
    markWalkable(14, 13, 15);
    markWalkable(13, 13, 15);
    markWalkable(12, 12, 14); // road curves left
    markWalkable(11, 12, 14);
    markWalkable(10, 11, 13); // cottage door area

    const grid = new CollisionGrid(w, h, data, TITLE_GRID_TILE);
    const pathfinder = new Pathfinder(grid);
    const tilePath = pathfinder.findPath(
      WALKER_START_TILE.x, WALKER_START_TILE.y,
      WALKER_END_TILE.x, WALKER_END_TILE.y,
    );

    if (!tilePath || tilePath.length === 0) return FALLBACK_PATH_WAYPOINTS;

    const simplified = Pathfinder.simplifyPath(tilePath);
    const half = TITLE_GRID_TILE / 2;
    return simplified.map(t => ({
      x: (t.x * TITLE_GRID_TILE + half) / TITLE_CARD_W,
      y: (t.y * TITLE_GRID_TILE + half) / TITLE_CARD_H,
    }));
  }

  /**
   * Spawn a character that walks the dirt-road path in the title art.
   * Delegates animation, shadow, and autonomous loop to WalkerAnimator.
   */
  private spawnWalker(): void {
    if (!this.textures.exists('waker-walk-up')) return;

    this.walkerPathWaypoints = this.computeWalkerPath();

    const animations: WalkerAnimationDef[] = [
      { key: 'title-walk-up', textureKey: 'waker-walk-up', startFrame: 0, endFrame: 11, frameRate: 15, repeat: -1 },
      { key: 'title-walk-down', textureKey: 'waker-walk-down', startFrame: 0, endFrame: 11, frameRate: 15, repeat: -1 },
      { key: 'title-walk-left', textureKey: 'waker-walk-left', startFrame: 0, endFrame: 5, frameRate: WALKER_LEFT_ANIM_FPS, repeat: -1 },
      { key: 'title-walk-right', textureKey: 'waker-walk-left', startFrame: 0, endFrame: 5, frameRate: WALKER_LEFT_ANIM_FPS, repeat: -1 },
      { key: 'title-talk-down', textureKey: 'waker-talk', startFrame: 0, endFrame: 7, frameRate: 15, repeat: -1 },
      { key: 'title-idle', textureKey: 'waker-idle', startFrame: 0, endFrame: 7, frameRate: 15, repeat: -1 },
    ];

    const animator = new WalkerAnimator({
      scene: this,
      perspective: TITLE_CARD_PERSPECTIVE,
      animKeys: {
        walkUp: 'title-walk-up',
        walkDown: 'title-walk-down',
        walkLeft: 'title-walk-left',
        walkRight: 'title-walk-right',
        idle: 'title-idle',
        talk: 'title-talk-down',
      },
      animations,
      spriteTextureKey: 'waker-walk-up',
      spriteDepth: 4,
      shadowDepth: 3,
      initialAlpha: 0,
    });

    this.walkerAnimator = animator;

    const startWP = this.walkerPathWaypoints[0];
    animator.setPosition(startWP.x, startWP.y);
    this.playerNX = startWP.x;
    this.playerNY = startWP.y;

    // Fade the walker + shadow in with the art
    this.tweens.add({
      targets: [animator.sprite, animator.shadow],
      alpha: { from: 0, to: 0.85 },
      duration: FADE_IN_MS,
      ease: 'Sine.easeIn',
    });

    // Start autonomous walk cycle
    animator.startAutonomous({
      waypoints: this.walkerPathWaypoints,
      speed: WALKER_PATH_SPEED,
      pauseDurationMs: WALKER_TALK_DURATION_MS,
      offScreenWaitMs: WALKER_OFFSCREEN_WAIT_MS,
      pauseAnimKey: 'title-talk-down',
    });
    this.walkerState = WalkerState.WALKING_UP;
  }



  update(_time: number, delta: number): void {
    const w = this.scale.width;

    this.parallaxManager?.tickAmbient(_time);
    if (this.grassMaskGfx && this.grassOverlay) {
      this.grassMaskGfx.x = this.grassOverlay.x - w / 2;
    }

    if (!this.walkerAnimator) return;

    // Player-controlled movement
    if (this.walkerState === WalkerState.PLAYER_CONTROLLED) {
      this.updatePlayerMovement(delta);
      this.updateCottageProximity();

      // Check for E key to enter cottage
      if (this.nearCottage && this.input.keyboard) {
        const eKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.E, false);
        if (Phaser.Input.Keyboard.JustDown(eKey)) {
          this.handleCottageEnter();
        }
      }
      return;
    }

    // Idle title state — walker just breathes, no autonomous movement
    if (this.walkerState === WalkerState.IDLE_TITLE) {
      this.walkerAnimator.update(delta);
      return;
    }

    // Autonomous walking — fully delegated to WalkerAnimator
    this.walkerAnimator.update(delta);
  }

  // ── Title text overlay ──────────────────────────────────────────────────────

  /** Render game logo over the sky (upper third), or text fallback. */
  private renderTitleText(): void {
    const cx = this.scale.width / 2;
    const titleY = this.scale.height * 0.20;

    if (this.textures.exists('game-logo')) {
      const logo = this.add.image(cx, titleY, 'game-logo');
      logo.setOrigin(0.5, 0.5);
      logo.setDepth(5);
      logo.setAlpha(0);

      // Scale logo to ~150px display height (native 313px)
      const targetHeight = 150;
      const scaleFactor = targetHeight / logo.height;
      logo.setScale(scaleFactor);

      this.logoImage = logo;
      this.titleTextObjects.push(logo);

      const subtitle = this.add.text(cx, titleY + 85, 'When the Fire Rests', {
        fontFamily: TITLE_FONT,
        fontSize: '18px',
        color: COLOR_INK,
        fontStyle: 'italic',
        shadow: {
          offsetX: 1,
          offsetY: 1,
          color: COLOR_BG_DEEP,
          blur: 2,
          fill: true,
        },
      });
      subtitle.setOrigin(0.5, 0.5);
      subtitle.setDepth(5);
      subtitle.setAlpha(0);
      this.titleTextObjects.push(subtitle);

      this.time.delayedCall(TITLE_TEXT_DELAY_MS, () => {
        this.tweens.add({
          targets: [logo, subtitle],
          alpha: 1,
          duration: 800,
          ease: 'Sine.easeIn',
        });
      });
      return;
    }

    // Fallback: text title if logo asset is missing
    if (!this.textures.exists('title-card')) return;

    const title2 = this.add.text(cx, titleY, 'The Small Hours', {
      fontFamily: TITLE_FONT,
      fontSize: '52px',
      color: COLOR_INK,
      shadow: {
        offsetX: 2,
        offsetY: 2,
        color: COLOR_BG_DEEP,
        blur: 4,
        fill: true,
      },
    });
    title2.setOrigin(0.5, 0.5);
    title2.setDepth(5);
    title2.setAlpha(0);
    this.titleTextObjects.push(title2);

    const subtitle2 = this.add.text(cx, titleY + 38, 'When the Fire Rests', {
      fontFamily: TITLE_FONT,
      fontSize: '18px',
      color: COLOR_INK,
      fontStyle: 'italic',
      shadow: {
        offsetX: 1,
        offsetY: 1,
        color: COLOR_BG_DEEP,
        blur: 2,
        fill: true,
      },
    });
    subtitle2.setOrigin(0.5, 0.5);
    subtitle2.setDepth(5);
    subtitle2.setAlpha(0);
    this.titleTextObjects.push(subtitle2);

    this.time.delayedCall(TITLE_TEXT_DELAY_MS, () => {
      this.tweens.add({
        targets: [title2, subtitle2],
        alpha: 1,
        duration: 800,
        ease: 'Sine.easeIn',
      });
    });
  }

  // ── Enter button pulse ──────────────────────────────────────────────────────

  /** Add breathing alpha pulse and hover scale to the enter button. */
  private addBreathingPulse(btn: TitleButton): void {
    // Breathing alpha oscillation
    this.tweens.add({
      targets: btn.label,
      alpha: { from: 0.85, to: 1.0 },
      yoyo: true,
      repeat: -1,
      duration: 1500,
      ease: 'Sine.easeInOut',
    });

    // Hover scale-up
    const originalScaleX = btn.label.scaleX;
    const originalScaleY = btn.label.scaleY;
    btn.hit.on('pointerover', () => {
      this.tweens.add({
        targets: btn.label,
        scaleX: originalScaleX * 1.05,
        scaleY: originalScaleY * 1.05,
        duration: 200,
        ease: 'Sine.easeOut',
      });
    });
    btn.hit.on('pointerout', () => {
      this.tweens.add({
        targets: btn.label,
        scaleX: originalScaleX,
        scaleY: originalScaleY,
        duration: 200,
        ease: 'Sine.easeOut',
      });
    });
  }

  // ── Music ───────────────────────────────────────────────────────────────────

  /**
   * Loop the title theme at a low volume. Browser autoplay policy will keep
   * it suspended until the first user gesture — Phaser's WebAudio backend
   * resumes the context on the first input automatically.
   */
  private startTitleMusic(): void {
    if (!this.cache.audio.exists('title-theme')) return;
    const muted = this.isMuted();
    const settings = loadSettings();
    this.musicVolume = settings.musicVolume;
    const effectiveVolume = settings.masterVolume * this.musicVolume;
    this.titleMusic = this.sound.add('title-theme', {
      loop: true,
      volume: effectiveVolume,
      mute: muted,
    });
    this.titleMusic.play();

    this.game.events.on('audio:muted-changed', this.onMuteChanged, this);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.stopTitleMusic());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.stopTitleMusic());
  }

  private onMuteChanged(muted: boolean): void {
    if (this.titleMusic && 'mute' in this.titleMusic) {
      (this.titleMusic as { mute: boolean }).mute = muted;
    }
  }

  private stopTitleMusic(): void {
    this.game.events.off('audio:muted-changed', this.onMuteChanged, this);
    const sound = this.titleMusic;
    this.titleMusic = null;
    if (!sound) return;
    try {
      sound.stop();
      sound.destroy();
    } catch {
      // already stopped or destroyed
    }
  }

  // ── Title art ───────────────────────────────────────────────────────────────

  /** Render the title-card image full-frame, or a styled fallback if the asset is missing. */
  private renderTitleArt(): void {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    if (this.textures.exists('title-card')) {
      const card = this.add.image(cx, cy, 'title-card', 'title-card/0');
      card.setName('title-card-image');
      const scaleX = this.scale.width / TITLE_CARD_W;
      const scaleY = this.scale.height / TITLE_CARD_H;
      const scale = Math.max(scaleX, scaleY);
      card.setScale(scale);
      card.setOrigin(0.5, 0.5);
      card.setDepth(0);

      // Background animation overlays
      this.spawnOverlayLayers(scale);
      return;
    }

    // Fallback: styled text title centred on screen.
    const fallbackTitle = this.add.text(cx, cy - 24, 'The Small Hours', {
      fontFamily: TITLE_FONT,
      fontSize: '64px',
      color: COLOR_INK,
    });
    fallbackTitle.setOrigin(0.5, 0.5);
    this.titleTextObjects.push(fallbackTitle);
    const fallbackSub = this.add.text(cx, cy + 36, 'When the Fire Rests', {
      fontFamily: TITLE_FONT,
      fontSize: '20px',
      color: COLOR_INK,
      fontStyle: 'italic',
    });
    fallbackSub.setOrigin(0.5, 0.5);
    this.titleTextObjects.push(fallbackSub);
  }

  /** Layer animated overlays (clouds, grass, trees) on top of the title card. */
  private spawnOverlayLayers(_artScale: number): void {
    const cx = this.scale.width / 2;
    const w = this.scale.width;
    const h = this.scale.height;

    const mgr = new ParallaxLayerManager(this, 'title');
    this.parallaxManager = mgr;

    const cloudKeys = ['title-clouds-1', 'title-clouds-2', 'title-clouds-3'] as const;
    const cloudYFracs = [0.12, 0.18, 0.15];
    const speeds = [3, 5, 4];
    for (let i = 0; i < cloudKeys.length; i++) {
      if (!this.textures.exists(cloudKeys[i])) continue;
      const cloud = this.add.image(cx + (i - 1) * w * 0.3, h * cloudYFracs[i], cloudKeys[i]);
      cloud.setOrigin(0.5, 0.5);
      cloud.setDepth(1);
      cloud.setAlpha(0.85);
      mgr.addExternalLayer({
        id: `cloud-${i + 1}`,
        parallax: 1,
        depth: 1,
        alpha: 0.85,
        ambient: { kind: 'linear-drift', amplitude: speeds[i], frequencyHz: 1, phaseRad: 0 },
      }, cloud);
    }

    if (this.textures.exists('title-trees-overlay')) {
      const treeImg = this.textures.get('title-trees-overlay').getSourceImage() as HTMLImageElement;
      const treeH = treeImg.height ?? 300;
      const tree = this.add.image(w / 2, h / 2 + treeH / 2, 'title-trees-overlay');
      tree.setOrigin(0, 1);
      tree.setDepth(3);
      mgr.addExternalLayer({
        id: 'trees',
        parallax: 1,
        depth: 3,
        alpha: 1,
        ambient: { kind: 'rotate-sway', amplitude: 0.012, frequencyHz: 0.25, phaseRad: 0.7 },
      }, tree);
    }

    if (this.textures.exists('title-grass-overlay')) {
      this.grassOverlay = this.add.image(cx, h + 100, 'title-grass-overlay');
      this.grassOverlay.setOrigin(0.5, 1);
      this.grassOverlay.setDepth(6);
      mgr.addExternalLayer({
        id: 'grass',
        parallax: 1,
        depth: 6,
        alpha: 1,
        ambient: { kind: 'horizontal-sway', amplitude: 3, frequencyHz: 0.1, phaseRad: 0 },
      }, this.grassOverlay);

      this.grassMaskGfx = this.make.graphics({ x: 0, y: 0 });
      this.rebuildGrassMask(w, h);
      this.grassOverlay.setMask(this.grassMaskGfx.createGeometryMask());
    }
  }

  // ── Buttons ─────────────────────────────────────────────────────────────────

  /** Rebuild the grass mask rectangles for current dimensions. */
  private rebuildGrassMask(w: number, h: number): void {
    if (!this.grassMaskGfx || !this.grassOverlay) return;
    const pathLeftEdge = w * 0.36;
    const pathRightEdge = w * 0.64;
    const grassTop = h + 100 - this.grassOverlay.displayHeight;
    const grassBottom = h + 100;

    this.grassMaskGfx.clear();
    this.grassMaskGfx.fillStyle(0xffffff);
    // Left grass block
    this.grassMaskGfx.fillRect(0, grassTop, pathLeftEdge, grassBottom - grassTop);
    // Right grass block
    this.grassMaskGfx.fillRect(pathRightEdge, grassTop, w - pathRightEdge, grassBottom - grassTop);
  }

  /** Primary action button — bottom-center. */
  private renderEnterButton(): TitleButton {
    const cx = this.scale.width / 2;
    const cy = this.scale.height - 88;
    return this.makeButton(cx, cy, 'Enter the world', () => this.handleEnter());
  }

  // ── UI Panel (mute, volume, settings, devworld) ─────────────────────────────

  /**
   * Group mute toggle, volume slider, and devworld button into a
   * semi-transparent panel positioned bottom-right.
   */
  private renderUIPanel(): Phaser.GameObjects.Container {
    const container = this.add.container(0, 0);
    container.setDepth(10);

    // Panel internal layout — items stacked vertically
    const innerW = SLIDER_W + 50; // enough for slider + label
    let yOffset = PANEL_PADDING;

    // Mute toggle
    const muteBtn = this.createMuteToggle(PANEL_PADDING, yOffset);
    container.add([muteBtn.hit, muteBtn.label]);
    yOffset += 22;

    // Volume slider
    const sliderObjects = this.createVolumeSlider(PANEL_PADDING, yOffset);
    container.add(sliderObjects);
    yOffset += 22;

    // Settings button
    const settingsBtn = this.createSettingsPanelButton(PANEL_PADDING, yOffset);
    container.add([settingsBtn.hit, settingsBtn.label]);
    yOffset += 22;

    // Devworld button
    const devBtn = this.createDevworldButton(PANEL_PADDING, yOffset);
    container.add([devBtn.hit, devBtn.label]);
    yOffset += 22;

    // Scene selector button
    const scenesBtn = this.createSceneSelectorButton(PANEL_PADDING, yOffset);
    container.add([scenesBtn.hit, scenesBtn.label]);
    yOffset += 20 + PANEL_PADDING;

    // Background panel — rounded rectangle via Graphics
    const panelW = innerW + PANEL_PADDING * 2;
    const panelH = yOffset;
    const bg = this.add.graphics();
    bg.fillStyle(PANEL_BG_COLOR, PANEL_BG_ALPHA);
    bg.fillRoundedRect(0, 0, panelW, panelH, PANEL_BORDER_RADIUS);
    bg.lineStyle(PANEL_BORDER_WIDTH, PANEL_BORDER_COLOR, 0.5);
    bg.strokeRoundedRect(0, 0, panelW, panelH, PANEL_BORDER_RADIUS);
    container.addAt(bg, 0); // behind other elements

    // Position container at bottom-right
    const panelX = this.scale.width - panelW - PANEL_MARGIN;
    const panelY = this.scale.height - panelH - PANEL_MARGIN;
    container.setPosition(panelX, panelY);

    return container;
  }

  /** Create mute toggle inside the UI panel (relative coords). */
  private createMuteToggle(x: number, y: number): TitleButton {
    const muted = this.isMuted();
    const hit = this.add.rectangle(x, y, 120, 20, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    const label = this.add.text(x + 4, y + 2, this.muteLabel(muted), {
      fontFamily: TITLE_FONT,
      fontSize: '11px',
      color: COLOR_INK,
    });
    label.setOrigin(0, 0);

    hit.on('pointerover', () => label.setColor(COLOR_INK_HOVER));
    hit.on('pointerout', () => label.setColor(COLOR_INK));
    hit.on('pointerdown', () => this.handleMuteToggle());

    this.muteButton = { hit, label };
    return this.muteButton;
  }

  /** Create volume slider inside the UI panel (relative coords). */
  private createVolumeSlider(x: number, y: number): Phaser.GameObjects.GameObject[] {
    const settings = loadSettings();
    this.musicVolume = settings.musicVolume;

    const labelX = x + 4;
    const sliderX = x + 40;

    const volLabel = this.add.text(labelX, y + 2, `${Math.round(this.musicVolume * 100)}%`, {
      fontFamily: TITLE_FONT,
      fontSize: '10px',
      color: COLOR_INK,
    }).setOrigin(0, 0);
    this.sliderLabel = volLabel;

    const track = this.add.rectangle(sliderX, y + 8, SLIDER_W, SLIDER_H, SLIDER_TRACK_COLOR)
      .setOrigin(0, 0.5);
    this.sliderTrack = track;

    const fillW = Math.max(1, SLIDER_W * this.musicVolume);
    const fill = this.add.rectangle(sliderX, y + 8, fillW, SLIDER_H, SLIDER_FILL_COLOR)
      .setOrigin(0, 0.5);
    this.sliderFill = fill;

    const hit = this.add.rectangle(sliderX, y + 8, SLIDER_W, 24, 0x000000, 0)
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true, draggable: true });
    this.sliderHit = hit;

    const updateFromPointer = (pointerX: number) => {
      // Convert pointer world coords to local panel coords
      const parent = hit.parentContainer;
      const baseX = parent ? parent.x + sliderX : sliderX;
      const localX = pointerX - baseX;
      const ratio = Math.max(0, Math.min(1, localX / SLIDER_W));
      this.setMusicVolume(ratio);
    };

    hit.on('pointerdown', (pointer: Phaser.Input.Pointer) => updateFromPointer(pointer.x));
    hit.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) updateFromPointer(pointer.x);
    });

    return [track, fill, hit, volLabel];
  }

  /** Create settings button inside the UI panel (relative coords). */
  private createSettingsPanelButton(x: number, y: number): TitleButton {
    const hit = this.add.rectangle(x, y, 120, 20, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    const label = this.add.text(x + 4, y + 2, 'settings', {
      fontFamily: TITLE_FONT,
      fontSize: '11px',
      color: COLOR_INK,
    });
    label.setOrigin(0, 0);

    hit.on('pointerover', () => label.setColor(COLOR_INK_HOVER));
    hit.on('pointerout', () => label.setColor(COLOR_INK));
    hit.on('pointerdown', () => this.handleSettings());

    return { hit, label };
  }

  /** Create devworld button inside the UI panel (relative coords). */
  private createDevworldButton(x: number, y: number): TitleButton {
    const hit = this.add.rectangle(x, y, 120, 20, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    const label = this.add.text(x + 4, y + 2, 'devworld', {
      fontFamily: TITLE_FONT,
      fontSize: '11px',
      color: COLOR_INK,
    });
    label.setOrigin(0, 0);

    hit.on('pointerover', () => label.setColor(COLOR_INK_HOVER));
    hit.on('pointerout', () => label.setColor(COLOR_INK));
    hit.on('pointerdown', () => this.handleDevworld());

    return { hit, label };
  }

  private createSceneSelectorButton(x: number, y: number): TitleButton {
    const hit = this.add.rectangle(x, y, 120, 20, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    const label = this.add.text(x + 4, y + 2, 'scenes', {
      fontFamily: TITLE_FONT,
      fontSize: '11px',
      color: COLOR_INK,
    });
    label.setOrigin(0, 0);

    hit.on('pointerover', () => label.setColor(COLOR_INK_HOVER));
    hit.on('pointerout', () => label.setColor(COLOR_INK));
    hit.on('pointerdown', () => {
      this.stopTitleMusic();
      this.scene.start(SCENE_SELECTOR_SCENE_KEY);
    });

    return { hit, label };
  }

  private muteLabel(muted: boolean): string {
    return muted ? 'sound: off' : 'sound: on';
  }

  /** Update music volume from slider, apply to playing music, persist. */
  private setMusicVolume(volume: number): void {
    this.musicVolume = volume;

    if (this.sliderFill) {
      this.sliderFill.width = Math.max(1, SLIDER_W * volume);
    }
    if (this.sliderLabel) {
      this.sliderLabel.setText(`${Math.round(volume * 100)}%`);
    }

    const settings = loadSettings();
    const effectiveVolume = settings.masterVolume * volume;
    if (this.titleMusic && 'setVolume' in this.titleMusic) {
      (this.titleMusic as Phaser.Sound.WebAudioSound).setVolume(effectiveVolume);
    }

    saveSettings({ ...settings, musicVolume: volume });
  }

  private isMuted(): boolean {
    const fromRegistry = this.registry.get('app:audioMuted');
    if (typeof fromRegistry === 'boolean') return fromRegistry;
    try {
      return localStorage.getItem(MUTE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private handleMuteToggle(): void {
    const next = !this.isMuted();
    this.registry.set('app:audioMuted', next);
    try {
      localStorage.setItem(MUTE_STORAGE_KEY, next ? '1' : '0');
    } catch {
      // localStorage may be unavailable
    }
    if (this.muteButton) {
      this.muteButton.label.setText(this.muteLabel(next));
    }
    this.game.events.emit('audio:muted-changed', next);
  }

  private handleSettings(): void {
    this.game.events.emit('title:settings-requested');
  }

  /** Enter devworld — skip character creation, go straight to ThresholdScene. */
  private handleDevworld(): void {
    this.stopTitleMusic();
    this.scene.start(THRESHOLD_SCENE_KEY, { firstDay: true, roomType: 'devworld' });
  }

  /**
   * "Enter the World" → dissolve UI and give player control.
   * The title screen IS the game.
   */
  private handleEnter(): void {
    if (this.enterInProgress) return;
    this.enterInProgress = true;
    this.menuState = MenuState.ENTERING_WORLD;
    this.enterGameplay();
  }

  /** Handle WASD/arrow input and click-to-move path following. */
  private updatePlayerMovement(delta: number): void {
    if (!this.walkerAnimator) return;

    const speed = PLAYER_MOVE_SPEED * (delta / 1000);
    let dx = 0;
    let dy = 0;
    let moved = false;

    // WASD / arrow key input
    const wDown = this.wasdKeys?.W.isDown || this.cursors?.up?.isDown;
    const sDown = this.wasdKeys?.S.isDown || this.cursors?.down?.isDown;
    const aDown = this.wasdKeys?.A.isDown || this.cursors?.left?.isDown;
    const dDown = this.wasdKeys?.D.isDown || this.cursors?.right?.isDown;

    if (wDown || sDown || aDown || dDown) {
      this.clickTargetPath = null;

      if (wDown) dy -= speed;
      if (sDown) dy += speed;
      if (aDown) dx -= speed;
      if (dDown) dx += speed;

      if (dx !== 0 && dy !== 0) {
        const len = Math.sqrt(dx * dx + dy * dy);
        dx = (dx / len) * speed;
        dy = (dy / len) * speed;
      }

      moved = true;
    } else if (this.clickTargetPath && this.clickTargetPath.length > 1) {
      this.clickPathProgress += PLAYER_MOVE_SPEED * (delta / 1000) * 1.2;
      if (this.clickPathProgress >= 1) {
        const lastWP = this.clickTargetPath[this.clickTargetPath.length - 1];
        this.playerNX = lastWP.x;
        this.playerNY = lastWP.y;
        this.clickTargetPath = null;
        moved = false;
      } else {
        const pos = interpolateWaypoints(this.clickTargetPath, this.clickPathProgress);
        dx = pos.x - this.playerNX;
        dy = pos.y - this.playerNY;
        this.playerNX = pos.x;
        this.playerNY = pos.y;
        moved = true;
      }
    }

    if (moved && this.clickTargetPath === null) {
      const newNX = this.playerNX + dx;
      const newNY = this.playerNY + dy;

      if (this.collisionGrid) {
        const tileX = Math.floor(newNX * TITLE_CARD_W / TITLE_GRID_TILE);
        const tileY = Math.floor(newNY * TITLE_CARD_H / TITLE_GRID_TILE);
        if (this.collisionGrid.isWalkable(tileX, tileY)) {
          this.playerNX = newNX;
          this.playerNY = newNY;
        }
      } else {
        this.playerNX += dx;
        this.playerNY += dy;
      }
    }

    this.playerNX = Math.max(0.30, Math.min(0.65, this.playerNX));
    this.playerNY = Math.max(0.60, Math.min(0.98, this.playerNY));

    // First movement: fade out WASD hint (GDD §3.3 step 5)
    if (moved && !this.hasMovedOnce) {
      this.hasMovedOnce = true;
      if (this.wasdIndicator) {
        this.tweens.add({
          targets: this.wasdIndicator,
          alpha: 0,
          duration: 800,
          ease: 'Sine.easeOut',
          onComplete: () => { if (this.wasdIndicator) { this.wasdIndicator.destroy(); this.wasdIndicator = null; } },
        });
      }
    }

    // Delegate position, animation, perspective, and shadow to WalkerAnimator
    this.walkerAnimator.applyMovement(this.playerNX, this.playerNY, moved, dx, dy);
  }

  /** Player reached the cottage — gateway to the deeper game. */
  private handleCottageEnter(): void {
    if (!this.isPlaying) return;
    this.isPlaying = false;

    // Gentle fade to save-slot selection (the deeper game)
    if (this.cottagePrompt) {
      this.tweens.add({ targets: this.cottagePrompt, alpha: 0, duration: 300 });
    }
    if (this.cottageGlow) {
      this.tweens.add({ targets: this.cottageGlow, alpha: 0, duration: 300 });
    }

    // Walker turns to face the door
    if (this.walkerAnimator && this.anims.exists('title-walk-up')) {
      this.walkerAnimator.sprite.play('title-walk-up');
      this.walkerAnimator.sprite.anims.stop();
    }

    this.time.delayedCall(500, () => {
      this.cameras.main.fadeOut(800, 0, 0, 0);
      this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
        // Go to save slot selection or character creation
        this.menuState = MenuState.MAIN_MENU;
        this.enterInProgress = false;
        this.isPlaying = false;
        this.walkerState = WalkerState.IDLE_TITLE;

        // Rebuild the scene with save-slot UI visible
        this.scene.restart({ showSaveSlots: true });
      });
    });
  }

  /**
   * GDD §3.3 seamless transition — dissolve all UI chrome and hand control
   * to the player. The painted title-card background stays. No scene switch.
   * No loading screen. Music continues. "You were always already in the world."
   */
  private enterGameplay(): void {
    // Phase 1 (0–800ms): Fade out logo + enter button + title text
    const p1Targets: Phaser.GameObjects.GameObject[] = [];
    if (this.logoImage) p1Targets.push(this.logoImage);
    if (this.enterButton) {
      this.tweens.killTweensOf(this.enterButton.label);
      this.tweens.killTweensOf(this.enterButton.hit);
      this.enterButton.hit.disableInteractive();
      p1Targets.push(this.enterButton.hit, this.enterButton.label);
    }
    p1Targets.push(...this.titleTextObjects);
    if (p1Targets.length > 0) {
      this.tweens.add({ targets: p1Targets, alpha: 0, duration: DISSOLVE_P1_MS, ease: 'Sine.easeOut' });
    }

    // Phase 2 (800–1600ms): Fade out UI panel
    this.time.delayedCall(DISSOLVE_P1_MS, () => {
      if (this.uiPanel) {
        this.tweens.add({ targets: this.uiPanel, alpha: 0, duration: DISSOLVE_P2_MS, ease: 'Sine.easeOut' });
      }
    });

    // Phase 3 (1600–2400ms): Walker stops autonomous loop, faces the camera
    this.time.delayedCall(DISSOLVE_P1_MS + DISSOLVE_P2_MS, () => {
      if (this.walkerAnimator) {
        const w = this.scale.width;
        const h = this.scale.height;
        this.playerNX = this.walkerAnimator.sprite.x / w;
        this.playerNY = this.walkerAnimator.sprite.y / h;
        this.playerNX = Math.max(0.30, Math.min(0.65, this.playerNX));
        this.playerNY = Math.max(0.60, Math.min(0.98, this.playerNY));

        this.walkerAnimator.stopAutonomous();
        this.walkerAnimator.setPosition(this.playerNX, this.playerNY);
        this.walkerAnimator.playIdle();
        this.walkerState = WalkerState.IDLE_TITLE;

        if (this.walkerAnimator.sprite.alpha < 0.1) this.walkerAnimator.sprite.setAlpha(0.85);
        if (this.walkerAnimator.shadow.alpha < 0.1) this.walkerAnimator.shadow.setAlpha(0.85);
      }
    });

    // Phase 4 (2400–3200ms): Init player input, show WASD hint, transfer control
    this.time.delayedCall(DISSOLVE_P1_MS + DISSOLVE_P2_MS + DISSOLVE_P3_MS, () => {
      // Build collision grid + pathfinder for player movement
      this.buildPlayableGrid();

      // Init keyboard input
      if (this.input.keyboard) {
        this.cursors = this.input.keyboard.createCursorKeys();
        this.wasdKeys = {
          W: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W, false),
          A: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A, false),
          S: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S, false),
          D: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D, false),
        };
      }

      // Click-to-move
      this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        if (this.walkerState === WalkerState.PLAYER_CONTROLLED) {
          this.handleClickToMove(pointer);
        }
      });

      // Show WASD hint
      const cx = this.scale.width / 2;
      const cy = this.scale.height - 60;
      this.wasdIndicator = this.add.text(cx, cy, 'WASD to move', {
        fontFamily: TITLE_FONT,
        fontSize: '16px',
        color: COLOR_INK,
        shadow: { offsetX: 1, offsetY: 1, color: '#000000', blur: 3, fill: true },
      }).setOrigin(0.5, 0.5).setDepth(15).setAlpha(0);
      this.tweens.add({ targets: this.wasdIndicator, alpha: 1, duration: 400, ease: 'Sine.easeIn' });

      // Transfer control
      this.walkerState = WalkerState.PLAYER_CONTROLLED;
      this.isPlaying = true;
      this.hasMovedOnce = false;

      // Spawn cottage door glow
      this.spawnCottageInteraction();
    });
  }

  /** Build the collision grid and pathfinder for player movement. */
  private buildPlayableGrid(): void {
    const w = TITLE_GRID_W;
    const h = TITLE_GRID_H;
    const data = new Array(w * h).fill(1);

    const markWalkable = (row: number, colStart: number, colEnd: number) => {
      for (let c = colStart; c <= colEnd; c++) {
        if (c >= 0 && c < w && row >= 0 && row < h) {
          data[row * w + c] = 0;
        }
      }
    };

    // Road corridor matching the title-card art — widened for comfortable play
    markWalkable(15, 12, 16);
    markWalkable(14, 12, 16);
    markWalkable(13, 12, 16);
    markWalkable(12, 11, 15);
    markWalkable(11, 11, 15);
    markWalkable(10, 10, 14);

    this.collisionGrid = new CollisionGrid(w, h, data, TITLE_GRID_TILE);
    this.pathfinder = new Pathfinder(this.collisionGrid);
  }

  /** Handle click-to-move: pathfind from current position to click target. */
  private handleClickToMove(pointer: Phaser.Input.Pointer): void {
    if (!this.pathfinder || !this.collisionGrid) return;

    const w = this.scale.width;
    const h = this.scale.height;

    // Convert click to tile coords
    const clickTileX = Math.floor((pointer.x / w) * TITLE_CARD_W / TITLE_GRID_TILE);
    const clickTileY = Math.floor((pointer.y / h) * TITLE_CARD_H / TITLE_GRID_TILE);

    // Convert current position to tile coords
    const curTileX = Math.floor(this.playerNX * TITLE_CARD_W / TITLE_GRID_TILE);
    const curTileY = Math.floor(this.playerNY * TITLE_CARD_H / TITLE_GRID_TILE);

    // Check if target is walkable
    if (!this.collisionGrid.isWalkable(clickTileX, clickTileY)) return;

    const tilePath = this.pathfinder.findPath(curTileX, curTileY, clickTileX, clickTileY);
    if (!tilePath || tilePath.length === 0) return;

    const simplified = Pathfinder.simplifyPath(tilePath);
    const half = TITLE_GRID_TILE / 2;
    this.clickTargetPath = simplified.map(t => ({
      x: (t.x * TITLE_GRID_TILE + half) / TITLE_CARD_W,
      y: (t.y * TITLE_GRID_TILE + half) / TITLE_CARD_H,
    }));
    this.clickPathProgress = 0;
  }

  /** Spawn a subtle glow near the cottage door to guide the player. */
  private spawnCottageInteraction(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    const cx = COTTAGE_DOOR_NX * w;
    const cy = COTTAGE_DOOR_NY * h;

    this.cottageGlow = this.add.ellipse(cx, cy, 40, 20, 0xeae0c8, 0.0);
    this.cottageGlow.setDepth(2);

    // Gentle pulse
    this.tweens.add({
      targets: this.cottageGlow,
      alpha: { from: 0.0, to: 0.3 },
      yoyo: true,
      repeat: -1,
      duration: 2000,
      ease: 'Sine.easeInOut',
      delay: 2000,
    });
  }

  /** Check proximity to cottage and show/hide interaction prompt. */
  private updateCottageProximity(): void {
    const dx = this.playerNX - COTTAGE_DOOR_NX;
    const dy = this.playerNY - COTTAGE_DOOR_NY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const wasNear = this.nearCottage;
    this.nearCottage = dist < COTTAGE_INTERACT_RADIUS;

    if (this.nearCottage && !wasNear) {
      const w = this.scale.width;
      const h = this.scale.height;
      if (!this.cottagePrompt) {
        this.cottagePrompt = this.add.text(
          COTTAGE_DOOR_NX * w,
          COTTAGE_DOOR_NY * h - 40,
          'Press E to enter',
          {
            fontFamily: TITLE_FONT,
            fontSize: '14px',
            color: COLOR_INK,
            shadow: { offsetX: 1, offsetY: 1, color: '#000000', blur: 3, fill: true },
          },
        ).setOrigin(0.5, 0.5).setDepth(15).setAlpha(0);
      }
      this.tweens.add({ targets: this.cottagePrompt, alpha: 1, duration: 300, ease: 'Sine.easeIn' });
    } else if (!this.nearCottage && wasNear && this.cottagePrompt) {
      this.tweens.add({ targets: this.cottagePrompt, alpha: 0, duration: 300, ease: 'Sine.easeOut' });
    }
  }

  // ── Save-slot UI ──────────────────────────────────────────────────────────

  private buildSaveSlotUI(): void {
    if (this.saveSlotContainer) {
      this.saveSlotContainer.destroy();
      this.saveSlotContainer = null;
    }

    const cx = this.scale.width / 2;
    const startY = this.scale.height * 0.32;
    const container = this.add.container(0, 0);
    container.setDepth(12);
    container.setAlpha(0);

    // Title
    const heading = this.add.text(cx, startY - 40, 'Choose your journey', {
      fontFamily: TITLE_FONT,
      fontSize: '24px',
      color: COLOR_INK,
    }).setOrigin(0.5, 0.5);
    container.add(heading);

    const slots = loadAllSlots();
    const count = getSlotCount();

    for (let i = 0; i < count; i++) {
      const slotY = startY + i * (SLOT_CARD_H + SLOT_CARD_GAP);
      this.buildSlotCard(container, i, slots[i], cx, slotY);
    }

    // Back button
    const backY = startY + count * (SLOT_CARD_H + SLOT_CARD_GAP) + 16;
    const backLabel = this.add.text(cx, backY, '\u2190 Back', {
      fontFamily: TITLE_FONT,
      fontSize: '18px',
      color: COLOR_INK,
    }).setOrigin(0.5, 0.5);
    const backHit = this.add.rectangle(cx, backY, 120, 32, 0x000000, 0)
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });
    backHit.on('pointerover', () => backLabel.setColor(COLOR_INK_HOVER));
    backHit.on('pointerout', () => backLabel.setColor(COLOR_INK));
    backHit.on('pointerdown', () => this.handleBackToMain());
    container.add([backHit, backLabel]);

    this.saveSlotContainer = container;

    // Fade in
    this.tweens.add({
      targets: container,
      alpha: 1,
      duration: 400,
      ease: 'Sine.easeIn',
    });
  }

  private buildSlotCard(
    container: Phaser.GameObjects.Container,
    index: number,
    data: SaveSlotData | null,
    cx: number,
    cy: number,
  ): void {
    // C5 "Indigo & Ink": the slot is a wax seal, its save details a paper tag.
    // Phaser-drawn rather than DOM because the title flow owns camera fades and
    // depth ordering — see ui/c5/C5SaveSlots.ts for why.
    if (isC5UiEnabled()) {
      buildC5SlotCard(this, container, {
        index,
        data,
        cx,
        cy,
        width: SLOT_CARD_W,
        height: SLOT_CARD_H,
        font: TITLE_FONT,
        onSelect: () => this.handleSlotSelect(index, data),
        onDelete: () => {
          if (data) this.showDeleteConfirm(index, data.characterName);
        },
      });
      return;
    }

    const left = cx - SLOT_CARD_W / 2;

    // Card background
    const bg = this.add.rectangle(cx, cy, SLOT_CARD_W, SLOT_CARD_H, SLOT_CARD_BG, SLOT_CARD_BG_ALPHA)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(1, SLOT_CARD_BORDER);

    // Hit area covers entire card
    const hit = this.add.rectangle(cx, cy, SLOT_CARD_W, SLOT_CARD_H, 0x000000, 0)
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });

    hit.on('pointerover', () => bg.setStrokeStyle(2, SLOT_CARD_BORDER_HOVER));
    hit.on('pointerout', () => bg.setStrokeStyle(1, SLOT_CARD_BORDER));
    hit.on('pointerdown', () => this.handleSlotSelect(index, data));

    container.add([bg, hit]);

    if (data) {
      // Filled slot: name, vocation, playtime, last played
      const nameText = this.add.text(left + 16, cy - 20, data.characterName, {
        fontFamily: TITLE_FONT,
        fontSize: '20px',
        color: COLOR_INK,
      }).setOrigin(0, 0);

      const vocationText = this.add.text(left + 16, cy + 6, data.vocation, {
        fontFamily: TITLE_FONT,
        fontSize: '14px',
        color: '#a0a0c0',
      }).setOrigin(0, 0);

      const playStr = formatPlaytime(data.playtimeSeconds);
      const lastStr = formatLastPlayed(data.lastPlayedISO);
      const metaText = this.add.text(
        cx + SLOT_CARD_W / 2 - 16,
        cy - 16,
        `${playStr}\n${lastStr}`,
        {
          fontFamily: TITLE_FONT,
          fontSize: '13px',
          color: '#a0a0c0',
          align: 'right',
        },
      ).setOrigin(1, 0);

      // Delete button (small X)
      const delHit = this.add.rectangle(
        cx + SLOT_CARD_W / 2 - 16,
        cy + SLOT_CARD_H / 2 - 14,
        24, 24, 0x000000, 0,
      )
        .setOrigin(0.5, 0.5)
        .setInteractive({ useHandCursor: true });
      const delLabel = this.add.text(
        cx + SLOT_CARD_W / 2 - 16,
        cy + SLOT_CARD_H / 2 - 14,
        '\u2715',
        { fontFamily: TITLE_FONT, fontSize: '14px', color: '#804040' },
      ).setOrigin(0.5, 0.5);
      delHit.on('pointerover', () => delLabel.setColor('#ff6060'));
      delHit.on('pointerout', () => delLabel.setColor('#804040'));
      delHit.on('pointerdown', (
        _pointer: Phaser.Input.Pointer,
        _lx: number,
        _ly: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        event.stopPropagation();
        this.showDeleteConfirm(index, data.characterName);
      });

      container.add([nameText, vocationText, metaText, delHit, delLabel]);
    } else {
      // Empty slot
      const emptyText = this.add.text(cx, cy, SLOT_EMPTY_TEXT, {
        fontFamily: TITLE_FONT,
        fontSize: '20px',
        color: COLOR_INK,
        fontStyle: 'italic',
      }).setOrigin(0.5, 0.5);
      container.add(emptyText);
    }

    // Slot number label
    const slotLabel = this.add.text(left + 8, cy - SLOT_CARD_H / 2 + 6, `Slot ${index + 1}`, {
      fontFamily: TITLE_FONT,
      fontSize: '11px',
      color: '#606080',
    }).setOrigin(0, 0);
    container.add(slotLabel);
  }

  private handleSlotSelect(index: number, data: SaveSlotData | null): void {
    if (this.menuState !== MenuState.SAVE_SLOTS) return;

    if (data) {
      // Existing save — load it and enter world
      this.transitionToWorld({ slotIndex: index, characterName: data.characterName, vocation: data.vocation });
    } else {
      // Empty slot — go to character creation with the selected slot index
      this.transitionToCharacterCreation(index);
    }
  }

  private transitionToCharacterCreation(slotIndex: number): void {
    this.menuState = MenuState.ENTERING_WORLD;
    this.enterInProgress = true;
    this.stopTitleMusic();
    this.cameras.main.fadeOut(600, 0, 0, 0);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      if (this.scene.get(CHARACTER_CREATION_SCENE_KEY)) {
        this.scene.start(CHARACTER_CREATION_SCENE_KEY, { firstDay: true, slotIndex });
        return;
      }
      this.scene.start(THRESHOLD_SCENE_KEY, { firstDay: true, slotIndex });
    });
  }

  /**
   * GDD §3.3 seamless dissolve: 5-phase UI dissolve that launches
   * OverworldScene in the background and hands off once it signals ready.
   * The title-card art background persists visually throughout — no black
   * screen, no loading screen.
   */
  private transitionToWorld(opts: { slotIndex: number; characterName: string; vocation: string; firstDay?: boolean }): void {
    this.menuState = MenuState.ENTERING_WORLD;
    this.enterInProgress = true;
    this.dissolveActive = true;
    this.overworldReady = false;
    this.dissolvePhaseComplete = false;

    // SMA-68 — always launch PaintedWorldCoordinator (OverworldScene removed).
    this.scene.launch(PAINTED_WORLD_COORDINATOR_SCENE_KEY, {
      firstDay: opts.firstDay ?? false,
      slotIndex: opts.slotIndex,
      characterName: opts.characterName,
      vocation: opts.vocation,
    });

    // Listen for overworld ready signal via registry
    const onReadyChange = (_parent: unknown, value: unknown) => {
      if (value === true) {
        this.overworldReady = true;
        this.registry.events.off('changedata-overworld:ready', onReadyChange);
        this.tryDissolveFinish();
      }
    };
    this.registry.events.on('changedata-overworld:ready', onReadyChange);
    // Race-condition guard: already set before listener attached
    if (this.registry.get('overworld:ready') === true) {
      this.overworldReady = true;
      this.registry.events.off('changedata-overworld:ready', onReadyChange);
    }

    // ── Phase 1 (0–800ms): Logo + enter button fade ──
    const p1Targets: Phaser.GameObjects.GameObject[] = [];
    if (this.logoImage) p1Targets.push(this.logoImage);
    if (this.enterButton) {
      // Kill the infinite breathing-pulse yoyo so the fade-out isn't
      // immediately overwritten by alpha oscillation on the next frame.
      this.tweens.killTweensOf(this.enterButton.label);
      this.tweens.killTweensOf(this.enterButton.hit);
      this.enterButton.hit.disableInteractive();
      p1Targets.push(this.enterButton.hit, this.enterButton.label);
    }
    // Also fade save-slot container if visible
    if (this.saveSlotContainer) p1Targets.push(this.saveSlotContainer);
    if (p1Targets.length > 0) {
      this.tweens.add({
        targets: p1Targets,
        alpha: 0,
        duration: DISSOLVE_P1_MS,
        ease: 'Sine.easeOut',
      });
    }

    // ── Phase 2 (800–1600ms): Options panel + title text dissolve ──
    const t2 = this.time.delayedCall(DISSOLVE_P1_MS, () => {
      const p2Targets: Phaser.GameObjects.GameObject[] = [];
      if (this.uiPanel) p2Targets.push(this.uiPanel);
      p2Targets.push(...this.titleTextObjects);
      if (p2Targets.length > 0) {
        this.tweens.add({
          targets: p2Targets,
          alpha: 0,
          duration: DISSOLVE_P2_MS,
          ease: 'Sine.easeOut',
        });
      }
      // Fade out title music during phase 2
      this.fadeTitleMusic(DISSOLVE_P2_MS);
    });
    this.dissolveTimers.push(t2);

    // ── Phase 3 (1600–2400ms): Walker stops loop, turns to idle ──
    const t3 = this.time.delayedCall(DISSOLVE_P1_MS + DISSOLVE_P2_MS, () => {
      if (this.walkerAnimator) {
        this.walkerAnimator.stopAutonomous();
        this.walkerState = WalkerState.IDLE_TITLE;
        if (this.anims.exists('title-walk-down')) {
          this.walkerAnimator.sprite.play('title-walk-down');
          this.walkerAnimator.sprite.anims.stop();
        }
        if (this.walkerAnimator.sprite.alpha < 0.1) this.walkerAnimator.sprite.setAlpha(0.85);
      }
    });
    this.dissolveTimers.push(t3);

    // ── Phase 4 (2400–3200ms): Brief WASD indicator ──
    const t4 = this.time.delayedCall(DISSOLVE_P1_MS + DISSOLVE_P2_MS + DISSOLVE_P3_MS, () => {
      const cx = this.scale.width / 2;
      const cy = this.scale.height - 60;
      this.wasdIndicator = this.add.text(cx, cy, 'WASD to move', {
        fontFamily: TITLE_FONT,
        fontSize: '16px',
        color: COLOR_INK,
        shadow: {
          offsetX: 1,
          offsetY: 1,
          color: '#000000',
          blur: 3,
          fill: true,
        },
      }).setOrigin(0.5, 0.5).setDepth(15).setAlpha(0);

      this.tweens.add({
        targets: this.wasdIndicator,
        alpha: 1,
        duration: 400,
        ease: 'Sine.easeIn',
      });
    });
    this.dissolveTimers.push(t4);

    // ── Phase 5 marker: dissolve phases complete ──
    const t5 = this.time.delayedCall(
      DISSOLVE_P1_MS + DISSOLVE_P2_MS + DISSOLVE_P3_MS + DISSOLVE_P4_MS,
      () => {
        this.dissolvePhaseComplete = true;
        this.tryDissolveFinish();
      },
    );
    this.dissolveTimers.push(t5);

    // ── Timeout: reassurance at 8s ──
    const tReassure = this.time.delayedCall(DISSOLVE_REASSURE_MS, () => {
      if (this.dissolveActive && !this.overworldReady) {
        const cx = this.scale.width / 2;
        const cy = this.scale.height / 2 + 40;
        this.reassuranceText = this.add.text(cx, cy, 'The world stirs — it knows you are near.', {
          fontFamily: TITLE_FONT,
          fontSize: '16px',
          color: COLOR_INK,
          fontStyle: 'italic',
          shadow: {
            offsetX: 1,
            offsetY: 1,
            color: '#000000',
            blur: 2,
            fill: true,
          },
        }).setOrigin(0.5, 0.5).setDepth(15).setAlpha(0);
        this.tweens.add({
          targets: this.reassuranceText,
          alpha: 1,
          duration: 600,
          ease: 'Sine.easeIn',
        });
      }
    });
    this.dissolveTimers.push(tReassure);

    // ── Timeout: bail at 20s ──
    const tBail = this.time.delayedCall(DISSOLVE_BAIL_MS, () => {
      if (this.dissolveActive && !this.overworldReady) {
        this.showReturnToTitle();
      }
    });
    this.dissolveTimers.push(tBail);
  }

  /** Attempt to finish the dissolve: both phases done AND overworld ready. */
  private tryDissolveFinish(): void {
    if (!this.dissolveActive) return;
    if (!this.dissolvePhaseComplete || !this.overworldReady) return;
    this.dissolveActive = false;

    // Fade out any remaining dissolve UI (WASD, reassurance)
    const fadeOut: Phaser.GameObjects.GameObject[] = [];
    if (this.wasdIndicator) fadeOut.push(this.wasdIndicator);
    if (this.reassuranceText) fadeOut.push(this.reassuranceText);
    if (this.walkerAnimator) {
      fadeOut.push(this.walkerAnimator.sprite);
      fadeOut.push(this.walkerAnimator.shadow);
    }

    // Fade remaining overlay elements (clouds, grass, trees)
    if (this.parallaxManager) {
      for (const layer of this.parallaxManager.layers) {
        if (layer.image) fadeOut.push(layer.image);
      }
    }

    if (fadeOut.length > 0) {
      this.tweens.add({
        targets: fadeOut,
        alpha: 0,
        duration: 600,
        ease: 'Sine.easeOut',
      });
    }

    // After overlays fade, stop TitleScene — OverworldScene takes over
    this.time.delayedCall(650, () => {
      this.cleanupDissolve();
      this.scene.stop(TITLE_SCENE_KEY);
    });
  }

  /** Show a "Return to title" button on 20s timeout. */
  private showReturnToTitle(): void {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2 + 80;
    this.returnButton = this.makeButton(cx, cy, 'Return to title', () => {
      this.cleanupDissolve();
      // Stop whichever world scene was launched (tilemap or painted shell).
      if (this.scene.isActive(PAINTED_WORLD_COORDINATOR_SCENE_KEY)) {
        this.scene.stop(PAINTED_WORLD_COORDINATOR_SCENE_KEY);
      }
      this.registry.remove('overworld:ready');
      this.scene.start(TITLE_SCENE_KEY);
    });
    this.returnButton.hit.setDepth(16);
    this.returnButton.label.setDepth(16);
    this.returnButton.hit.setAlpha(0);
    this.returnButton.label.setAlpha(0);
    this.tweens.add({
      targets: [this.returnButton.hit, this.returnButton.label],
      alpha: 1,
      duration: 400,
      ease: 'Sine.easeIn',
    });
  }

  /** Fade title music to zero over the given duration. */
  private fadeTitleMusic(durationMs: number): void {
    if (!this.titleMusic || !('volume' in this.titleMusic)) {
      this.stopTitleMusic();
      return;
    }
    const sound = this.titleMusic as Phaser.Sound.WebAudioSound;
    const startVol = sound.volume;
    let counter: Phaser.Tweens.Tween | null = null;
    counter = this.tweens.addCounter({
      from: 0,
      to: 100,
      duration: durationMs,
      ease: 'Sine.easeOut',
      onUpdate: () => {
        try {
          const t = (counter?.getValue() ?? 100) / 100;
          sound.setVolume(startVol * (1 - t));
        } catch { /* destroyed */ }
      },
      onComplete: () => this.stopTitleMusic(),
    });
  }

  /** Clean up dissolve state — safe to call multiple times. */
  private cleanupDissolve(): void {
    for (const t of this.dissolveTimers) {
      if (t && !t.hasDispatched) t.destroy();
    }
    this.dissolveTimers = [];
    if (this.wasdIndicator) { this.wasdIndicator.destroy(); this.wasdIndicator = null; }
    if (this.reassuranceText) { this.reassuranceText.destroy(); this.reassuranceText = null; }
    if (this.returnButton) {
      this.returnButton.hit.destroy();
      this.returnButton.label.destroy();
      this.returnButton = null;
    }
  }

  // ── Delete confirmation dialog ──────────────────────────────────────────

  private showDeleteConfirm(slotIndex: number, characterName: string): void {
    if (this.confirmContainer) {
      this.confirmContainer.destroy();
      this.confirmContainer = null;
    }

    this.menuState = MenuState.DELETE_CONFIRM;
    this.pendingDeleteIndex = slotIndex;

    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;
    const container = this.add.container(0, 0);
    container.setDepth(20);

    // Dim overlay
    const overlay = this.add.rectangle(cx, cy, this.scale.width, this.scale.height, 0x000000, 0.5)
      .setOrigin(0.5, 0.5)
      .setInteractive(); // blocks clicks to slots behind
    container.add(overlay);

    // Dialog box
    const dialogBg = this.add.rectangle(cx, cy, CONFIRM_W, CONFIRM_H, SLOT_CARD_BG, 0.95)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(1, SLOT_CARD_BORDER);
    container.add(dialogBg);

    const prompt = this.add.text(cx, cy - 30, `Delete "${characterName}"?`, {
      fontFamily: TITLE_FONT,
      fontSize: '18px',
      color: COLOR_INK,
      align: 'center',
      wordWrap: { width: CONFIRM_W - 32 },
    }).setOrigin(0.5, 0.5);
    container.add(prompt);

    const subtext = this.add.text(cx, cy, 'This cannot be undone.', {
      fontFamily: TITLE_FONT,
      fontSize: '13px',
      color: '#a0a0c0',
    }).setOrigin(0.5, 0.5);
    container.add(subtext);

    // Confirm button
    const confirmLabel = this.add.text(cx - 50, cy + 36, 'Delete', {
      fontFamily: TITLE_FONT,
      fontSize: '16px',
      color: '#ff6060',
    }).setOrigin(0.5, 0.5);
    const confirmHit = this.add.rectangle(cx - 50, cy + 36, 80, 28, 0x000000, 0)
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });
    confirmHit.on('pointerover', () => confirmLabel.setColor('#ff9090'));
    confirmHit.on('pointerout', () => confirmLabel.setColor('#ff6060'));
    confirmHit.on('pointerdown', () => this.confirmDelete());
    container.add([confirmHit, confirmLabel]);

    // Cancel button
    const cancelLabel = this.add.text(cx + 50, cy + 36, 'Cancel', {
      fontFamily: TITLE_FONT,
      fontSize: '16px',
      color: COLOR_INK,
    }).setOrigin(0.5, 0.5);
    const cancelHit = this.add.rectangle(cx + 50, cy + 36, 80, 28, 0x000000, 0)
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });
    cancelHit.on('pointerover', () => cancelLabel.setColor(COLOR_INK_HOVER));
    cancelHit.on('pointerout', () => cancelLabel.setColor(COLOR_INK));
    cancelHit.on('pointerdown', () => this.cancelDelete());
    container.add([cancelHit, cancelLabel]);

    // Fade in
    container.setAlpha(0);
    this.tweens.add({ targets: container, alpha: 1, duration: 200, ease: 'Sine.easeIn' });

    this.confirmContainer = container;
  }

  private confirmDelete(): void {
    if (this.pendingDeleteIndex >= 0) {
      deleteSlot(this.pendingDeleteIndex);
    }
    this.pendingDeleteIndex = -1;
    this.dismissConfirm();
    // Rebuild slot list to reflect deletion
    this.buildSaveSlotUI();
  }

  private cancelDelete(): void {
    this.pendingDeleteIndex = -1;
    this.dismissConfirm();
  }

  private dismissConfirm(): void {
    this.menuState = MenuState.SAVE_SLOTS;
    if (this.confirmContainer) {
      this.confirmContainer.destroy();
      this.confirmContainer = null;
    }
  }

  // ── Back to main menu ─────────────────────────────────────────────────────

  private handleBackToMain(): void {
    this.menuState = MenuState.MAIN_MENU;
    this.enterInProgress = false;

    // Fade out slot UI
    if (this.saveSlotContainer) {
      this.tweens.add({
        targets: this.saveSlotContainer,
        alpha: 0,
        duration: 300,
        ease: 'Sine.easeOut',
        onComplete: () => {
          if (this.saveSlotContainer) {
            this.saveSlotContainer.destroy();
            this.saveSlotContainer = null;
          }
        },
      });
    }

    // Fade main menu buttons back in
    const showTargets: Phaser.GameObjects.GameObject[] = [];
    if (this.enterButton) showTargets.push(this.enterButton.hit, this.enterButton.label);
    if (this.uiPanel) showTargets.push(this.uiPanel);
    if (showTargets.length > 0) {
      this.time.delayedCall(320, () => {
        this.tweens.add({ targets: showTargets, alpha: 1, duration: 400, ease: 'Sine.easeIn' });
      });
    }
  }

  /** Build a hit-rect-backed text button at (x, y). */
  private makeButton(
    x: number,
    y: number,
    text: string,
    onClick: () => void,
  ): TitleButton {
    const hit = this.add
      .rectangle(x, y, BUTTON_W, BUTTON_H, 0x000000, 0)
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(8);
    const label = this.add.text(x, y, text, {
      fontFamily: TITLE_FONT,
      fontSize: '20px',
      color: COLOR_INK,
    });
    label.setOrigin(0.5, 0.5).setDepth(8);

    hit.on('pointerover', () => label.setColor(COLOR_INK_HOVER));
    hit.on('pointerout', () => label.setColor(COLOR_INK));
    hit.on('pointerdown', onClick);

    return { hit, label };
  }
}
